import React, { useEffect, useMemo, useState } from "react";
import Header from "components/header/Header";
import Images from "assets/images";
import Spacer from "components/Spacer/Spacer";
import MedicalAssistant from "components/MedicalAssistant/MedicalAssistant";
import BottomFixedButton from "components/bottomFixedButton/BottomFixedButton";
import Card from "components/visitCommon/Card";
import RegularSelect from "components/radioButtonCard/RegularSelect";
import CheckBoxCard from "components/checkBoxCard/CheckBoxCard";
import SearchInputWithChipCard from "components/searchInputWithChip/SearchInputWithChipCard";
import RadioButtonCard from "components/radioButtonCard/RadioButtonCard";

import { useDermatologyVisitFlow } from "hooks/useVisitFlow";
import useUpdateDermatologyVisit from "hooks/visit/useUpdateDermatologyVisit";
import { dermatology_sub_steps } from "helpers/enum";
import scrollToBottom from "helpers/scrollToBottom";
import { useDispatch } from "react-redux";
import { dermaVisitFlowReducers } from "reducers/dermatology/dermaVisitFlowReducer";

import HistoryDebounceSearchModal from "../dermatology/medicalHistory/HistoryDebounceSearchModal";

const TOBACCO_FORMS = [
  "Cigarettes",
  "Vape",
  "Cannabis",
  "Chew",
  "Cigar",
  "Pipe",
];

function normalizeMedicalConditions(selected) {
  if (!Array.isArray(selected)) return [];
  return selected
    .filter(Boolean)
    .map((el) => {
      const category = el?.category ?? "Other";
      const disease = el?.disease ?? "";
      const id = el?.id ?? `${category}:${disease}`;
      return { ...el, category, disease, id };
    })
    .filter((el) => el?.disease?.trim()?.length);
}

function SocialHistoryForm({ onClose }) {
  const dispatch = useDispatch();

  const { incompleteData, subSteps } = useDermatologyVisitFlow();
  const { updateVisit, updatingVisit } = useUpdateDermatologyVisit();

  const screenValueFromStore =
    subSteps?.[dermatology_sub_steps.social_history]?.value;

  const [firstAnswer, setFirstAnswer] = useState("");
  const [secondAnswer, setSecondAnswer] = useState("");
  const [thirdAnswer, setThirdAnswer] = useState("");
  const [thirdYesAnswer, setThirdYesAnswer] = useState([]);
  const [fourthAnswer, setFourthAnswer] = useState("");
  const [fourthYesAnswer, setFourthYesAnswer] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const isValid = useMemo(() => {
    const firstOk = firstAnswer?.length > 0;
    const secondOk = secondAnswer?.length > 0;
    const thirdOk = thirdAnswer?.length > 0;

    const tobaccoOk =
      thirdAnswer === "No"
        ? true
        : thirdAnswer === "Yes"
        ? thirdYesAnswer?.length > 0
        : false;

    const medicalOk =
      fourthAnswer === "None"
        ? true
        : fourthAnswer?.length
        ? fourthYesAnswer?.length > 0
        : false;

    return firstOk && secondOk && thirdOk && tobaccoOk && medicalOk;
  }, [firstAnswer, secondAnswer, thirdAnswer, thirdYesAnswer, fourthAnswer, fourthYesAnswer]);

  function hydrateFromSocialOrMedicalHistory(arr) {
    if (!Array.isArray(arr) || !arr?.length) return;

    arr.forEach((question) => {
      switch (question?.health_profile_section) {
        case "exercise":
          setFirstAnswer(
            question?.answer?.[0] + question?.answer?.slice(1)?.toLowerCase()
          );
          break;
        case "alcohol":
          setSecondAnswer(question?.answer ?? "");
          break;
        case "tobacco":
          setThirdAnswer(
            question?.answer?.[0] + question?.answer?.slice(1)?.toLowerCase()
          );
          setThirdYesAnswer(question?.form_of_tobacco ?? []);
          break;
        case "medical_history": {
          const none = question?.answer === "NO";
          setFourthAnswer(none ? "None" : "Yes");

          if (question?.selected_options?.length > 0) {
            const selectedOptions = [];
            question?.selected_options?.forEach((option) => {
              option?.diseases?.forEach((disease) => {
                selectedOptions.push({
                  category: option?.category,
                  disease,
                  id: `${option?.category}:${disease}`,
                });
              });
            });
            setFourthYesAnswer(normalizeMedicalConditions(selectedOptions));
          } else {
            setFourthYesAnswer([]);
          }
          break;
        }
        default:
          break;
      }
    });
  }

  useEffect(() => {
    if (screenValueFromStore?.length) {
      hydrateFromSocialOrMedicalHistory(screenValueFromStore);
    } else if (incompleteData?.social_or_medical_history?.length) {
      hydrateFromSocialOrMedicalHistory(incompleteData?.social_or_medical_history);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOnSelectThirdYesAnswer(el) {
    let final = [...thirdYesAnswer];
    if (final?.includes(el)) {
      setThirdYesAnswer(final.filter((elem) => elem !== el));
    } else {
      setThirdYesAnswer([...final, el]);
    }
  }

  function buildSelectedOptions() {
    const refined = [];
    normalizeMedicalConditions(fourthYesAnswer).forEach((el) => {
      const existingIndex = refined?.findIndex(
        (answer) => answer.category === el.category
      );
      if (existingIndex > -1) {
        refined[existingIndex].diseases.push(el?.disease);
      } else {
        refined.push({
          category: el.category,
          diseases: [el?.disease],
        });
      }
    });
    return refined;
  }

  function onSubmit() {
    const selected_options = fourthAnswer === "None" ? [] : buildSelectedOptions();

    const payloadValue = [
      {
        health_profile_section: "exercise",
        question: "Do you exercise regularly?",
        answer_type_boolean: true,
        answer: firstAnswer.toUpperCase(),
      },
      {
        health_profile_section: "alcohol",
        question: "Do you drink alcohol?",
        answer_type_boolean: false,
        answer: secondAnswer,
      },
      {
        health_profile_section: "tobacco",
        question: "Do you smoke?",
        answer_type_boolean: true,
        answer: thirdAnswer.toUpperCase(),
        form_of_tobacco: thirdAnswer === "Yes" ? thirdYesAnswer : [],
      },
      {
        health_profile_section: "medical_history",
        question: "Current medical conditions under treatment",
        answer_type_boolean: true,
        answer: fourthAnswer === "None" ? "NO" : "YES",
        selected_options,
      },
    ];

    updateVisit({
      payload: {
        case: {
          id: incompleteData?.id,
          social_or_medical_history: payloadValue,
          dermatology_steps: incompleteData?.dermatology_steps,
        },
      },
      change_current_screen: false,
      screen_to_complete: dermatology_sub_steps.social_history,
      callBack: () => {
        dispatch(
          dermaVisitFlowReducers.social_history({
            value: payloadValue,
          })
        );
        onClose();
      },
    });
  }

  return (
    <div className="h-full bg-veryLightBlue">
      <Header
        title="Social History"
        hideBackBtn
        rightActionView={
          <img
            onClick={onClose}
            className="w-10 cursor-pointer"
            src={Images.closeIcon}
            alt={"close_icons"}
          />
        }
      />
      <div className="overflow-y-scroll wrapper h-eliminateHeaderWithFooter bg-blue">
        <div className="min-h-full px-10 py-8 pb-64">
          <MedicalAssistant
            content={
              <p>
                Before we proceed, please answer a few lifestyle and social history
                questions.
              </p>
            }
            className="mb-10"
          />

          <Card>
            <RegularSelect
              label="Do you exercise regularly?"
              options={["Yes", "No"]}
              selected={firstAnswer}
              setSelected={setFirstAnswer}
              orange={true}
            />

            <Spacer height={24} />

            <RegularSelect
              label="Do you drink alcohol?"
              options={["Never", "Occasionally", "Daily", "Previously/Quit"]}
              selected={secondAnswer}
              setSelected={setSecondAnswer}
              orange={true}
              mobile={true}
            />

            <Spacer height={24} />

            <RegularSelect
              label="Do you smoke?"
              options={["Yes", "No"]}
              selected={thirdAnswer}
              setSelected={(el) => {
                setThirdAnswer(el);
                if (el === "No") {
                  setThirdYesAnswer([]);
                }
              }}
              orange={true}
            />

            {thirdAnswer === "Yes" ? (
              <div>
                <Spacer height={20} />
                <CheckBoxCard
                  options={TOBACCO_FORMS}
                  selected={thirdYesAnswer}
                  onSelect={handleOnSelectThirdYesAnswer}
                  variant="small"
                />
              </div>
            ) : null}

            <Spacer height={24} />

            <p className="font-medium text-2xl">
              Please add any medical conditions for which you are under treatment:
            </p>

            <Spacer height={16} />

            <SearchInputWithChipCard
              inputLabel={"Search Medical Conditions"}
              onClickInput={() => {
                if (updatingVisit) return;
                setFourthAnswer("Yes");
                scrollToBottom();
                setShowSearchModal(true);
              }}
              selected={normalizeMedicalConditions(fourthYesAnswer)}
              setSelected={(next) =>
                setFourthYesAnswer(normalizeMedicalConditions(next))
              }
            />

            <Spacer height={16} />

            <RadioButtonCard
              options={["None"]}
              selected={fourthAnswer}
              onSelect={() => {
                if (fourthAnswer === "None") {
                  setFourthAnswer("Yes");
                } else {
                  setFourthAnswer("None");
                  setFourthYesAnswer([]);
                }
              }}
            />
          </Card>
        </div>
      </div>
      <BottomFixedButton
        type="submit"
        label="Next"
        onClick={onSubmit}
        disabled={!isValid}
        loading={updatingVisit}
      />
      {showSearchModal && (
        <div className="fixed inset-0 z-50">
          <HistoryDebounceSearchModal
            keyToSearch={"medical-history-options"}
            title={"Search Medical Conditions"}
            onClose={() => setShowSearchModal(false)}
            selected={normalizeMedicalConditions(fourthYesAnswer)}
            setSelected={(next) =>
              setFourthYesAnswer(normalizeMedicalConditions(next))
            }
          />
        </div>
      )}
    </div>
  );
}

export default SocialHistoryForm;
